import { useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { useGameStore } from '../store/gameStore';
import { terrainHeight } from './Terrain';

interface CityDecorationsProps {
  cityData: {
    districts: Array<{
      id: string;
      name: string;
      color: string;
      bounds: { x: [number, number]; z: [number, number] };
    }>;
  };
}

/**
 * CityDecorations — palms, paths, benches, and district archways
 * that make the city feel alive and festival-worthy.
 */
export default function CityDecorations({ cityData }: CityDecorationsProps) {
  // Generate palm tree positions (deterministic from city layout)
  const palms = useMemo(() => {
    const positions: Array<{ x: number; z: number; scale: number }> = [];
    let seed = 42;

    const rng = () => {
      seed = (seed * 16807) % 2147483647;
      return (seed - 1) / 2147483646;
    };

    // Scatter trees around the wider festival grounds
    for (let i = 0; i < 40; i++) {
      const x = -38 + rng() * 76;  // covers -38 to +38
      const z = -22 + rng() * 44;  // covers -22 to +22
      const scale = 0.5 + rng() * 1.2;

      // Avoid clustering too close to district centers
      positions.push({ x, z, scale });
    }

    return positions;
  }, []);

  // District boundary archways
  const archways = useMemo(() => {
    return cityData.districts.map((d) => ({
      name: d.name,
      color: d.color,
      x: (d.bounds.x[0] + d.bounds.x[1]) / 2,
      z: d.bounds.z[0] - 0.5,  // south entrance
    }));
  }, [cityData]);

  return (
    <group>
      {/* Trees */}
      {palms.map((p, i) => (
        <Tree key={`tree-${i}`} position={[p.x, 0, p.z]} scale={p.scale} />
      ))}

      {/* Dirt paths — horizontal boulevard through the city center */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.02, 0]} receiveShadow>
        <planeGeometry args={[60, 2.5]} />
        <meshStandardMaterial color="#c4a882" roughness={0.9} />
      </mesh>

      {/* Cross paths to each district */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[-14, 0.02, 0]} receiveShadow>
        <planeGeometry args={[2, 24]} />
        <meshStandardMaterial color="#c4a882" roughness={0.9} />
      </mesh>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[14, 0.02, 0]} receiveShadow>
        <planeGeometry args={[2, 24]} />
        <meshStandardMaterial color="#c4a882" roughness={0.9} />
      </mesh>

      {/* District archways */}
      {archways.map((a, i) => (
        <group key={`arch-${i}`} position={[a.x, 0, a.z]}>
          {/* Pillars */}
          <mesh position={[-1.5, 2, 0]} castShadow>
            <boxGeometry args={[0.3, 4, 0.3]} />
            <meshStandardMaterial color="#e8d5b7" roughness={0.4} />
          </mesh>
          <mesh position={[1.5, 2, 0]} castShadow>
            <boxGeometry args={[0.3, 4, 0.3]} />
            <meshStandardMaterial color="#e8d5b7" roughness={0.4} />
          </mesh>
          {/* Arch beam */}
          <mesh position={[0, 4.2, 0]} castShadow>
            <boxGeometry args={[3.6, 0.3, 0.5]} />
            <meshStandardMaterial color="#f5e6cc" roughness={0.3} />
          </mesh>
          {/* District name sign */}
          <mesh position={[0, 4.6, 0.35]}>
            <planeGeometry args={[2.5, 0.6]} />
            <meshBasicMaterial color={a.color} />
          </mesh>
        </group>
      ))}

      {/* Benches along the main path */}
      {[-8, -3, 3, 8].map((x, i) => {
        const z = i % 2 === 0 ? 1.8 : -1.8;
        const y = terrainHeight(x, z);
        return <Bench key={`bench-${i}`} position={[x, y, z]} rotation={[0, 0, 0]} />;
      })}

      {/* Festival string lights between buildings */}
      <FestivalLights />

      {/* Street lamps along main paths */}
      {[-22, -14, -6, 0, 8, 14, 22].map((x, i) => {
        const z = i % 2 === 0 ? 2 : -2;
        const y = terrainHeight(x, z);
        return <StreetLamp key={`lamp-${i}`} position={[x, y, z]} />;
      })}

      {/* Floating music notes near active building */}
      <FloatingNotes />
    </group>
  );
}

/**
 * A street lamp — pole with glowing light
 */
function StreetLamp({ position }: { position: [number, number, number] }) {
  return (
    <group position={position}>
      <mesh position={[0, 1.8, 0]} castShadow>
        <cylinderGeometry args={[0.08, 0.1, 3.6, 8]} />
        <meshStandardMaterial color="#3a3a3a" roughness={0.5} metalness={0.6} />
      </mesh>
      <mesh position={[0, 3.7, 0]} castShadow>
        <sphereGeometry args={[0.3, 8, 4, 0, Math.PI * 2, 0, Math.PI / 2]} />
        <meshStandardMaterial color="#4a4a4a" roughness={0.4} />
      </mesh>
      <mesh position={[0, 3.65, 0]}>
        <sphereGeometry args={[0.15, 8, 8]} />
        <meshBasicMaterial color="#FFF3CD" />
      </mesh>
      {/* Warm downward light cone */}
      <pointLight
        position={[0, 3.5, 0]}
        intensity={1.5}
        color="#ffdd99"
        distance={5}
        decay={2}
      />
    </group>
  );
}

/**
 * Floating music notes that appear near the currently-playing stage
 */
function FloatingNotes() {
  const activeTrackId = useGameStore((s) => s.activeTrackId);
  const lockedTrackId = useGameStore((s) => s.lockedTrackId);
  const cityData = useGameStore((s) => s.cityData);
  const trackId = lockedTrackId || activeTrackId;

  const stages = useMemo(() => {
    if (!cityData) return [];
    return cityData.districts.flatMap((d) =>
      d.tracks.map((t, i) => {
        const cols = Math.ceil(Math.sqrt(d.tracks.length));
        const row = Math.floor(i / cols);
        const col = i % cols;
        const xRange = d.bounds.x[1] - d.bounds.x[0];
        const zRange = d.bounds.z[1] - d.bounds.z[0];
        const cellW = xRange / cols;
        const cellD = zRange / Math.ceil(d.tracks.length / cols);
        return {
          trackId: t.id,
          x: d.bounds.x[0] + col * cellW + cellW / 2,
          z: d.bounds.z[0] + row * cellD + cellD / 2,
        };
      }),
    );
  }, [cityData]);

  const activeStage = stages.find((b) => b.trackId === trackId);
  if (!activeStage) return null;

  const notes = [
    { offset: [1.5, 4, 1.5], delay: 0 },
    { offset: [-1.2, 5, -1], delay: 0.4 },
    { offset: [0.8, 3.5, -1.3], delay: 0.2 },
    { offset: [-1, 4.5, 0.8], delay: 0.6 },
  ];

  return (
    <group position={[activeStage.x, 0, activeStage.z]}>
      {notes.map((note, i) => (
        <AnimatedNote key={i} offset={note.offset as [number, number, number]} delay={note.delay} />
      ))}
    </group>
  );
}

function AnimatedNote({ offset, delay }: { offset: [number, number, number]; delay: number }) {
  const ref = useRef<THREE.Group>(null);

  useFrame(({ clock }) => {
    if (!ref.current) return;
    const t = clock.getElapsedTime() + delay;
    ref.current.position.y = offset[1] + Math.sin(t * 2) * 0.5;
    ref.current.rotation.y = Math.sin(t * 0.7) * 0.3;
    ref.current.rotation.z = Math.cos(t * 0.5) * 0.2;
    ref.current.position.x = offset[0] + Math.cos(t * 0.8) * 0.3;
    ref.current.position.z = offset[2] + Math.sin(t * 0.6) * 0.3;
  });

  return (
    <group ref={ref} position={offset}>
      <mesh>
        <sphereGeometry args={[0.15, 8, 8]} />
        <meshBasicMaterial color="#FFD700" opacity={0.7} transparent />
      </mesh>
      <mesh position={[0.1, 0.15, 0]}>
        <cylinderGeometry args={[0.03, 0.03, 0.4, 6]} />
        <meshBasicMaterial color="#FFD700" opacity={0.6} transparent />
      </mesh>
    </group>
  );
}

/**
 * Realistic tree with clustered sphere foliage and tapered trunk.
 * Richer than the old cone-frond palm.
 */
function Tree({ position, scale }: { position: [number, number, number]; scale: number }) {
  const trunkH = 2.5 * scale;
  const trunkBase = 0.12 * scale;
  const trunkTop = 0.06 * scale;
  const canopyR = 0.9 * scale;

  // Generate foliage clusters at different positions (relative to trunk top group)
  const clusters = [
    { pos: [0, 0.3 * scale, 0], r: canopyR * 0.7 },
    { pos: [0.35 * scale, 0.6 * scale, 0.2 * scale], r: canopyR * 0.55 },
    { pos: [-0.3 * scale, 0.5 * scale, -0.15 * scale], r: canopyR * 0.55 },
    { pos: [0.15 * scale, 0.9 * scale, -0.25 * scale], r: canopyR * 0.45 },
    { pos: [-0.2 * scale, 0.75 * scale, 0.3 * scale], r: canopyR * 0.5 },
    { pos: [0, 0.15 * scale, 0.4 * scale], r: canopyR * 0.4 },
    { pos: [0.4 * scale, 0.4 * scale, -0.3 * scale], r: canopyR * 0.4 },
  ];

  return (
    <group position={position}>
      {/* Trunk — wider at base, narrow at top */}
      <mesh position={[0, trunkH / 2, 0]} castShadow>
        <cylinderGeometry args={[trunkTop, trunkBase, trunkH, 8]} />
        <meshStandardMaterial color="#5D4037" roughness={0.85} />
      </mesh>
      {/* Slight trunk bend */}
      <group position={[0, trunkH, 0]} rotation={[0.08 * scale, 0.05, 0.04]}>
        {/* Foliage clusters */}
        {clusters.map((c, i) => (
          <mesh key={`leaf-${i}`} position={c.pos as [number, number, number]} castShadow>
            <sphereGeometry args={[c.r, 8, 6]} />
            <meshStandardMaterial
              color={i % 3 === 0 ? '#2E7D32' : i % 3 === 1 ? '#388E3C' : '#43A047'}
              roughness={0.7}
            />
          </mesh>
        ))}
      </group>
    </group>
  );
}

/**
 * A simple park bench
 */
function Bench({ position, rotation }: { position: [number, number, number]; rotation: [number, number, number] }) {
  return (
    <group position={position} rotation={rotation as unknown as THREE.Euler}>
      {/* Seat */}
      <mesh position={[0, 0.6, 0]} castShadow>
        <boxGeometry args={[1.6, 0.08, 0.4]} />
        <meshStandardMaterial color="#6D4C41" roughness={0.5} />
      </mesh>
      {/* Legs */}
      <mesh position={[-0.65, 0.25, 0]} castShadow>
        <boxGeometry args={[0.1, 0.5, 0.35]} />
        <meshStandardMaterial color="#5D4037" roughness={0.6} />
      </mesh>
      <mesh position={[0.65, 0.25, 0]} castShadow>
        <boxGeometry args={[0.1, 0.5, 0.35]} />
        <meshStandardMaterial color="#5D4037" roughness={0.6} />
      </mesh>
      {/* Backrest */}
      <mesh position={[0, 0.85, -0.16]} castShadow>
        <boxGeometry args={[1.6, 0.4, 0.06]} />
        <meshStandardMaterial color="#6D4C41" roughness={0.5} />
      </mesh>
    </group>
  );
}

/**
 * Festival string lights strung between points
 */
function FestivalLights() {
  const points = [
    [-25, 5], [0, 8], [18, 6], [22, -3], [5, -10], [-18, -6],
  ];

  const bulbs = [];
  for (let i = 0; i < points.length - 1; i++) {
    const [x1, z1] = points[i];
    const [x2, z2] = points[i + 1];
    const segments = 6;
    for (let s = 0; s <= segments; s++) {
      const t = s / segments;
      const x = x1 + (x2 - x1) * t;
      const z = z1 + (z2 - z1) * t;
      // Catenary sag
      const sag = Math.sin(t * Math.PI) * 1.0;
      const colors = ['#FF6B6B', '#FFD93D', '#6BCB77', '#4D96FF', '#FF8E53', '#C084FC'];
      bulbs.push({ x, z, y: 6 + sag, color: colors[i % colors.length] });
    }
  }

  return (
    <group>
      {bulbs.map((b, i) => (
        <mesh key={`bulb-${i}`} position={[b.x, b.y, b.z]}>
          <sphereGeometry args={[0.1, 8, 8]} />
          <meshBasicMaterial color={b.color} />
        </mesh>
      ))}
      {/* Wire lines between bulbs */}
      {points.map((_, i) => {
        if (i === points.length - 1) return null;
        const [x1, z1] = points[i];
        const [x2, z2] = points[i + 1];
        const midX = (x1 + x2) / 2;
        const midZ = (z1 + z2) / 2;
        const dist = Math.sqrt((x2 - x1) ** 2 + (z2 - z1) ** 2);
        const angle = Math.atan2(x2 - x1, z2 - z1);
        return (
          <mesh
            key={`wire-${i}`}
            position={[midX, 6.5, midZ]}
            rotation={[0, angle, 0]}
          >
            <cylinderGeometry args={[0.015, 0.015, dist, 8]} />
            <meshBasicMaterial color="#ffffff" opacity={0.3} transparent />
          </mesh>
        );
      })}
    </group>
  );
}
